
import { useState, useEffect } from 'react';
import { 
  Settings, 
  ChevronDown, 
  BarChart4, 
  TrendingUp, 
  Calendar, 
  EyeOff,
  Zap,
  X
} from 'lucide-react';
import { StockData } from '../data/sampleData';
import { formatPercentChange, formatDate, formatCurrency, simulateLSTMPrediction } from '../utils/stockUtils';
import { cn } from '@/lib/utils';
import DatasetSelector from './DatasetSelector';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';

interface PredictionModelProps {
  stockData: StockData;
  onPredictionComplete?: () => void;
  externalTimeframe?: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All';
  onTimeframeChange?: (timeframe: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All') => void;
}

const PredictionModel = ({ 
  stockData, 
  onPredictionComplete,
  externalTimeframe,
  onTimeframeChange 
}: PredictionModelProps) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [predictionResults, setPredictionResults] = useState<any>(null);
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([stockData.id]);
  const [selectedModel, setSelectedModel] = useState<'lstm' | 'rl'>('lstm');
  const [predictionDays, setPredictionDays] = useState(30);
  const [timeframe, setTimeframe] = useState<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'>(externalTimeframe || '3M');
  const [modelParameters, setModelParameters] = useState({
    epochs: 100,
    layerSize: 64,
    dropout: 0.2,
    learningRate: 0.001
  });
  
  // Update local timeframe if external timeframe changes
  useEffect(() => {
    if (externalTimeframe) {
      setTimeframe(externalTimeframe);
    }
  }, [externalTimeframe]);
  
  const handleDatasetSelection = (selectedIds: string[]) => {
    if (selectedIds.length === 0) {
      // Always keep at least one dataset selected
      return;
    }
    setSelectedDatasets(selectedIds);
  };
  
  const runPrediction = () => {
    setIsLoading(true);
    
    // Simulate an API call/calculation delay
    setTimeout(() => {
      const predictions = simulateLSTMPrediction(stockData.data, predictionDays);
      
      // Calculate some mock accuracy metrics
      const confidence = 85 + Math.random() * 10;
      const error = 2 + Math.random() * 3;
      
      // Get the last actual data point and the first prediction
      const lastActual = stockData.data[stockData.data.length - 1];
      const firstPrediction = predictions[0];
      
      // Calculate change
      const changeDelta = firstPrediction.value - lastActual.value;
      const changePercent = (changeDelta / lastActual.value) * 100;
      
      // Update the stock data with predictions
      stockData.prediction = predictions;
      
      setPredictionResults({
        predictions,
        metrics: {
          confidence,
          error,
          rmse: error * 1.2,
          mape: error * 0.8,
        },
        summary: {
          startDate: formatDate(predictions[0].date),
          endDate: formatDate(predictions[predictions.length - 1].date),
          startValue: predictions[0].value,
          endValue: predictions[predictions.length - 1].value,
          changeDelta,
          changePercent,
        }
      });
      
      setIsLoading(false);
      
      // Notify parent that prediction is complete
      if (onPredictionComplete) {
        onPredictionComplete();
      }
    }, 2000);
  };
  
  // Prepare chart data by combining actual and prediction data
  const getChartData = () => {
    if (!stockData.data) return [];
    
    // Filter data based on timeframe
    let filteredData;
    const now = new Date();
    
    switch (timeframe) {
      case '1W':
        const oneWeekAgo = new Date(now);
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        filteredData = stockData.data.filter(p => new Date(p.date) >= oneWeekAgo);
        break;
      case '1M':
        const oneMonthAgo = new Date(now);
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        filteredData = stockData.data.filter(p => new Date(p.date) >= oneMonthAgo);
        break;
      case '3M':
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        filteredData = stockData.data.filter(p => new Date(p.date) >= threeMonthsAgo);
        break;
      case '6M':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        filteredData = stockData.data.filter(p => new Date(p.date) >= sixMonthsAgo);
        break;
      case '1Y':
        const oneYearAgo = new Date(now);
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        filteredData = stockData.data.filter(p => new Date(p.date) >= oneYearAgo);
        break;
      case 'All':
      default:
        filteredData = stockData.data;
    }
    
    // Ensure we have some data points
    if (filteredData.length === 0 && stockData.data.length > 0) {
      // If filtered data is empty, use at least the most recent points
      filteredData = stockData.data.slice(-30);
    }
    
    const historicalData = filteredData.map(point => ({
      date: point.date,
      value: point.value,
      type: 'actual'
    }));
    
    if (!predictionResults) return historicalData;
    
    const predictionData = predictionResults.predictions.map((point: any) => ({
      date: point.date,
      value: point.value,
      type: 'prediction'
    }));
    
    return [...historicalData, ...predictionData];
  };
  
  const handleTimeframeChange = (newTimeframe: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All') => {
    setTimeframe(newTimeframe);
    if (onTimeframeChange) {
      onTimeframeChange(newTimeframe);
    }
  };
  
  const CustomPredictionTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isPrediction = data.type === 'prediction';
      
      return (
        <div className="bg-card border p-3 rounded-lg shadow-md">
          <p className="text-sm font-medium mb-1">{formatDate(label)}</p>
          <p className="text-base font-semibold">{formatCurrency(data.value)}</p>
          
          {isPrediction && (
            <div className="mt-1.5">
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                Prediction
              </span>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };
  
  const chartData = getChartData();
  const isPositive = predictionResults?.summary?.changePercent >= 0;
  
  return (
    <div className="bg-card border rounded-xl overflow-hidden p-6 premium-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold">{stockData.name} Prediction</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Generate predictions using machine learning
          </p>
        </div>
        
        <button
          onClick={() => setIsSettingsOpen(!isSettingsOpen)}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
        >
          <Settings size={20} className="text-muted-foreground" />
        </button>
      </div>
      
      <div className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="text-sm font-medium">Prediction Model</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSelectedModel('lstm')}
                className={cn(
                  "p-3 rounded-lg flex items-center justify-center transition-all",
                  selectedModel === 'lstm' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:bg-muted/70"
                )}
              >
                <TrendingUp size={16} className="mr-2" />
                <span className="text-sm font-medium">LSTM</span>
              </button>
              
              <button
                onClick={() => setSelectedModel('rl')}
                className={cn(
                  "p-3 rounded-lg flex items-center justify-center transition-all",
                  selectedModel === 'rl' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:bg-muted/70"
                )}
              >
                <Zap size={16} className="mr-2" />
                <span className="text-sm font-medium">RL</span>
              </button>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="text-sm font-medium">Prediction Timeframe</div>
            <div className="grid grid-cols-4 gap-2">
              {[7, 30, 90, 180].map((days) => (
                <button
                  key={days}
                  onClick={() => setPredictionDays(days)}
                  className={cn(
                    "p-2 rounded-lg flex items-center justify-center transition-all",
                    predictionDays === days 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted text-muted-foreground hover:bg-muted/70"
                  )}
                >
                  <span className="text-sm font-medium">{days} days</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {isSettingsOpen && (
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg border animate-scale-in">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">Model Parameters</div>
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">Epochs</label>
                <input
                  type="number"
                  value={modelParameters.epochs}
                  onChange={(e) => setModelParameters({
                    ...modelParameters,
                    epochs: parseInt(e.target.value)
                  })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground">Layer Size</label>
                <input
                  type="number"
                  value={modelParameters.layerSize}
                  onChange={(e) => setModelParameters({
                    ...modelParameters,
                    layerSize: parseInt(e.target.value)
                  })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground">Dropout</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="0.9"
                  value={modelParameters.dropout}
                  onChange={(e) => setModelParameters({
                    ...modelParameters,
                    dropout: parseFloat(e.target.value)
                  })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground">Learning Rate</label>
                <input
                  type="number"
                  step="0.001"
                  min="0.0001"
                  max="0.1"
                  value={modelParameters.learningRate}
                  onChange={(e) => setModelParameters({
                    ...modelParameters,
                    learningRate: parseFloat(e.target.value)
                  })}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-3">
          <div className="text-sm font-medium">Historical Timeframe</div>
          <div className="flex flex-wrap gap-2">
            {(['1W', '1M', '3M', '6M', '1Y', 'All'] as const).map((option) => (
              <button
                key={option}
                onClick={() => handleTimeframeChange(option)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-all",
                  timeframe === option 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-transparent text-muted-foreground hover:bg-muted"
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        
        <button
          onClick={runPrediction}
          disabled={isLoading}
          className={cn(
            "w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center",
            isLoading 
              ? "bg-muted text-muted-foreground cursor-not-allowed"
              : "bg-primary text-primary-foreground hover:bg-primary/90"
          )}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-3"></div>
              Running Prediction...
            </>
          ) : (
            <>Generate Prediction</>
          )}
        </button>
      </div>
      
      {predictionResults && !isLoading && (
        <div className="mt-8 border-t pt-6 animate-fade-in">
          <h4 className="text-lg font-semibold mb-4">Prediction Results</h4>
          
          <div className="h-[300px] mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  scale="time" 
                  tick={{ fontSize: 10 }}
                  tickFormatter={(date) => {
                    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
                  }}
                />
                <YAxis 
                  domain={['auto', 'auto']}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip content={<CustomPredictionTooltip />} />
                <Legend />
                
                {/* Vertical divider line between actual and prediction */}
                {chartData.length > 0 && chartData.findIndex(d => d.type === 'prediction') >= 0 && (
                  <ReferenceLine
                    x={chartData.find(d => d.type === 'prediction')?.date}
                    stroke="#8884d8"
                    strokeDasharray="3 3"
                    label={{ value: 'Prediction Start', position: 'top', fill: '#8884d8' }}
                  />
                )}
                
                {/* Create two separate datasets for actual and predicted values */}
                <Area
                  type="monotone"
                  dataKey="value"
                  fill="url(#colorValue)"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: '#3b82f6' }}
                  name="Actual Price"
                  connectNulls
                  data={chartData.filter(d => d.type === 'actual')}
                />
                
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={isPositive ? "#10b981" : "#ef4444"}
                  strokeWidth={2}
                  dot={false}
                  name="Predicted Price"
                  connectNulls
                  data={chartData.filter(d => d.type === 'prediction')}
                />
                
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="rgba(59, 130, 246, 0.3)" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="rgba(59, 130, 246, 0)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Time Period</div>
                <div className="text-sm font-medium">
                  {predictionResults.summary.startDate} — {predictionResults.summary.endDate}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Predicted Price (End)</div>
                <div className="text-sm font-medium">
                  {formatCurrency(predictionResults.summary.endValue)}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Predicted Change</div>
                <div className={cn(
                  "text-sm font-medium",
                  predictionResults.summary.changePercent >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {formatPercentChange(predictionResults.summary.changePercent)}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Model Confidence</div>
                <div className="text-sm font-medium">
                  {predictionResults.metrics.confidence.toFixed(2)}%
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Mean Error (RMSE)</div>
                <div className="text-sm font-medium">
                  {predictionResults.metrics.rmse.toFixed(2)}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Mean Absolute % Error</div>
                <div className="text-sm font-medium">
                  {predictionResults.metrics.mape.toFixed(2)}%
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Model Type</div>
                <div className="text-sm font-medium capitalize">
                  {selectedModel} Neural Network
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Prediction Status</div>
                <div className="flex items-center">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                  <span className="text-sm font-medium">Complete</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-center space-x-3">
            <button className="text-sm flex items-center text-primary">
              <BarChart4 size={16} className="mr-1" />
              View Detailed Chart
            </button>
            <button className="text-sm flex items-center text-primary">
              <Calendar size={16} className="mr-1" />
              Export Results
            </button>
            <button className="text-sm flex items-center text-primary">
              <EyeOff size={16} className="mr-1" />
              Hide Results
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictionModel;
